// ─────────────────────────────────────────────────────────────────────────────
//  services/dataAdapter.js — Transforms relational query data → legacy UI shapes
//
//  The relational schema uses snake_case columns and UUID IDs. The existing UI
//  expects camelCase fields and treats IDs as opaque keys (Date.now() integers
//  historically, but UUIDs work fine as React keys / state lookups).
//
//  This adapter is the ONLY place shape translation happens. When the app
//  eventually adopts the relational shapes natively, this file shrinks to nothing.
// ─────────────────────────────────────────────────────────────────────────────

// ── Helpers ────────────────────────────────────────────────────────────────
const ts = (v) => v ? new Date(v).toISOString() : new Date().toISOString();
const num = (v) => parseFloat(v) || 0;

// ── Master data ────────────────────────────────────────────────────────────

export function adaptProducts(rows) {
  return (rows || []).map((r) => ({
    id: r.id,
    productName: r.product_name || '',
    category: r.category || '',
    unit: r.unit || 'piece',
    costPrice: num(r.cost_price),
    sellingPrice: num(r.selling_price),
    openingStockQty: num(r.opening_stock_qty),
    openingStockValue: num(r.opening_stock_value),
    stock: num(r.current_stock_qty ?? r.opening_stock_qty),
    rowVersion: r.row_version,
    createdAt: ts(r.created_at),
    updatedAt: ts(r.updated_at),
  }));
}

export function adaptCustomers(rows) {
  return (rows || []).map((r) => ({
    id: r.id,
    name: r.customer_name || '',
    phone: r.customer_phone || '',
    email: r.customer_email || '',
    address: r.address || '',
    tin: r.tin || '',
    vrn: r.vrn || '',
    notes: r.notes || '',
    rowVersion: r.row_version,
    createdAt: ts(r.created_at),
    updatedAt: ts(r.updated_at),
  }));
}

export function adaptSuppliers(rows) {
  return (rows || []).map((r) => ({
    id: r.id,
    name: r.supplier_name || '',
    contact: r.phone || '',
    phone: r.phone || '',
    email: r.email || '',
    address: r.address || '',
    rowVersion: r.row_version,
    createdAt: ts(r.created_at),
    updatedAt: ts(r.updated_at),
  }));
}

export function adaptExpenseCategories(rows) {
  return (rows || []).map((r) => ({
    id: r.id,
    name: r.name || '',
    desc: '',
    rowVersion: r.row_version,
    createdAt: ts(r.created_at),
    updatedAt: ts(r.updated_at),
  }));
}

export function adaptServices(rows) {
  return (rows || []).map((r) => ({
    id: r.id,
    name: r.name || '',
    defaultPrice: num(r.default_price),
    defaultCost: num(r.default_cost),
    rowVersion: r.row_version,
    createdAt: ts(r.created_at),
    updatedAt: ts(r.updated_at),
  }));
}

export function adaptPaymentAccounts(rows) {
  return (rows || []).map((r) => ({
    id: r.id,
    type: r.type || 'cash',
    bankName: r.bank_name || '',
    networkName: r.network_name || '',
    accountName: r.account_name || '',
    accountNumber: r.account_number || '',
    phoneNumber: r.phone_number || '',
    openingBalance: num(r.opening_balance),
    rowVersion: r.row_version,
    createdAt: ts(r.created_at),
    updatedAt: ts(r.updated_at),
  }));
}

// ── Transactions ───────────────────────────────────────────────────────────

function adaptPaymentRows(rows, dateField) {
  return (rows || []).map((p) => ({
    type: p.wallet_type || p.payment_method || 'cash',
    amount: num(p.amount),
    date: p.payment_date || '',
    method: p.payment_method || p.wallet_type || 'cash',
    paymentAccountId: p.payment_account_id,
  }));
}

export function adaptSales(rows) {
  return (rows || []).map((s) => {
    const items = (s.sale_items || []).map((it) => ({
      id: it.id,
      type: it.item_type || 'product',
      productName: it.product_name || '',
      productId: it.product_id,
      serviceId: it.service_id,
      description: it.description || it.product_name || '',
      quantity: num(it.quantity),
      cost: num(it.unit_cost),
      sellingPrice: num(it.selling_price),
    }));

    const payments = adaptPaymentRows(s.sale_payments);
    const primaryMethod = payments.length > 0 ? payments[0].type : 'cash';

    return {
      id: s.id,
      date: s.sale_date || '',
      customerName: s.customer_name || '',
      customerId: s.customer_id,
      totalRevenue: num(s.total_revenue),
      totalCost: num(s.total_cost),
      profit: num(s.profit),
      isPaid: s.is_paid,
      status: s.status,
      paymentMethod: primaryMethod,
      items,
      payments,
      productName: items[0]?.productName || '',
      quantity: items[0]?.quantity || 0,
      sellingPrice: items[0]?.sellingPrice || 0,
      invoiceNumber: s.invoice_number || '',
      invoiceIssued: !!s.invoice_number,
      rowVersion: s.row_version,
      createdAt: ts(s.created_at),
      updatedAt: ts(s.updated_at),
    };
  });
}

export function adaptPurchases(rows) {
  return (rows || []).map((p) => {
    const items = (p.purchase_items || []).map((it) => ({
      id: it.id,
      productName: it.product_name || '',
      productId: it.product_id,
      description: it.description || it.product_name || '',
      quantity: num(it.quantity),
      costPrice: num(it.unit_cost),
      cost: num(it.unit_cost),
      lineCost: num(it.line_cost),
      // Aliases required by frozen moneyMath.js engine (reads totalCost / unitPrice / unitCost)
      totalCost: num(it.line_cost),
      unitCost: num(it.unit_cost),
      unitPrice: num(it.unit_cost),
    }));

    const payments = adaptPaymentRows(p.purchase_payments);
    const primaryMethod = payments.length > 0 ? payments[0].type : 'cash';

    return {
      id: p.id,
      date: p.purchase_date || '',
      supplier: p.supplier_name || '',
      supplierId: p.supplier_id,
      totalCost: num(p.total_cost),
      isPaid: p.is_paid,
      status: p.status,
      paymentMethod: primaryMethod,
      items,
      payments,
      productName: items[0]?.productName || '',
      quantity: items[0]?.quantity || 0,
      costPrice: items[0]?.costPrice || 0,
      rowVersion: p.row_version,
      createdAt: ts(p.created_at),
      updatedAt: ts(p.updated_at),
    };
  });
}

export function adaptExpenses(rows) {
  return (rows || []).map((e) => {
    const payments = adaptPaymentRows(e.expense_payments);
    const primaryMethod = payments.length > 0 ? payments[0].type : 'cash';

    return {
      id: e.id,
      date: e.expense_date || '',
      category: e.category_name || '',
      categoryId: e.category_id,
      name: e.description || '',
      description: e.description || '',
      amount: num(e.amount),
      isPaid: e.is_paid,
      status: e.status,
      paymentMethod: primaryMethod,
      payments,
      rowVersion: e.row_version,
      createdAt: ts(e.created_at),
      updatedAt: ts(e.updated_at),
    };
  });
}

export function adaptServiceTransactions(rows) {
  return (rows || []).map((st) => {
    const payments = adaptPaymentRows(st.service_payments);
    const primaryMethod = payments.length > 0 ? payments[0].type : 'cash';

    return {
      id: st.id,
      date: st.txn_date || '',
      serviceName: st.service_name || '',
      serviceId: st.service_id,
      customerName: st.customer_name || '',
      customerId: st.customer_id,
      totalRevenue: num(st.total_revenue),
      totalCost: num(st.total_cost),
      profit: num(st.profit),
      isPaid: st.is_paid,
      status: st.status,
      paymentMethod: primaryMethod,
      payments,
      rowVersion: st.row_version,
      createdAt: ts(st.created_at),
      updatedAt: ts(st.updated_at),
    };
  });
}

export function adaptSalesReturns(rows) {
  return (rows || []).map((sr) => {
    const items = (sr.sales_return_items || []).map((it) => ({
      id: it.id,
      productName: it.product_name || '',
      productId: it.product_id,
      qty: num(it.quantity),
      quantity: num(it.quantity),
      unitCost: num(it.unit_cost),
      cost: num(it.unit_cost),
    }));

    return {
      id: sr.id,
      date: sr.return_date || '',
      originalSaleId: sr.original_sale_id,
      refundAmt: num(sr.refund_amount),
      totalRefund: num(sr.refund_amount),
      restock: sr.restock,
      reason: sr.reason || '',
      items,
      status: sr.status,
      rowVersion: sr.row_version,
      createdAt: ts(sr.created_at),
      updatedAt: ts(sr.updated_at),
    };
  });
}

export function adaptPurchaseReturns(rows) {
  return (rows || []).map((pr) => {
    const items = (pr.purchase_return_items || []).map((it) => ({
      id: it.id,
      productName: it.product_name || '',
      productId: it.product_id,
      qty: num(it.quantity),
      quantity: num(it.quantity),
      unitCost: num(it.unit_cost),
      cost: num(it.unit_cost),
    }));

    return {
      id: pr.id,
      date: pr.return_date || '',
      originalPurchaseId: pr.original_purchase_id,
      refundAmt: num(pr.refund_amount),
      totalRefund: num(pr.refund_amount),
      restock: pr.restock,
      reason: pr.reason || '',
      items,
      status: pr.status,
      rowVersion: pr.row_version,
      createdAt: ts(pr.created_at),
      updatedAt: ts(pr.updated_at),
    };
  });
}

export function adaptInvoices(rows) {
  return (rows || []).map((inv) => ({
    id: inv.id,
    invoiceNumber: inv.invoice_number || '',
    customerName: inv.customer_name || '',
    customerId: inv.customer_id,
    date: inv.invoice_date || '',
    totalRevenue: num(inv.total_amount),
    notes: inv.notes || '',
    isPaid: inv.status === 'paid',
    isManualInvoice: true,
    payments: (inv.invoice_deposits || []).map((d) => ({
      type: d.wallet_type || d.payment_method || 'cash',
      amount: num(d.amount),
      date: d.payment_date || '',
    })),
    status: inv.status,
    rowVersion: inv.row_version,
    createdAt: ts(inv.created_at),
    updatedAt: ts(inv.updated_at),
  }));
}

export function adaptCashAdjustments(rows) {
  return (rows || []).map((ca) => ({
    id: ca.id,
    type: ca.direction === 'in' ? 'credit' : 'debit',
    account: 'cash',
    amount: num(ca.amount),
    description: ca.reason || '',
    reason: ca.reason || '',
    date: ca.adjustment_date || '',
    systemGenerated: false,
    createdAt: ts(ca.created_at),
    updatedAt: ts(ca.updated_at),
  }));
}

// ── Bulk transformer ───────────────────────────────────────────────────────

/**
 * Transform the result of queries.all_business_data into legacy UI shapes.
 * Returns an object keyed by the collection names AppContext/App.js expect.
 */
export function adaptAllBusinessData(raw) {
  if (!raw || typeof raw !== 'object') return null;

  return {
    // AppContext collections
    products: adaptProducts(raw.products),
    sales: adaptSales(raw.sales),
    purchases: adaptPurchases(raw.purchases),
    expenses: adaptExpenses(raw.expenses),
    expenseCategories: adaptExpenseCategories(raw.expense_categories),
    suppliers: adaptSuppliers(raw.suppliers),
    customers: adaptCustomers(raw.customers),
    serviceTransactions: adaptServiceTransactions(raw.service_transactions),
    manualTransactions: adaptCashAdjustments(raw.cash_adjustments),

    // App.js collections
    services: adaptServices(raw.services),
    paymentAccounts: adaptPaymentAccounts(raw.payment_accounts),
    salesReturns: adaptSalesReturns(raw.sales_returns),
    purchaseReturns: adaptPurchaseReturns(raw.purchase_returns),
    manualInvoices: adaptInvoices(raw.invoices),
  };
}
